import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Shield, Globe, Zap, Lock, Server, Cloud, Settings, CheckCircle, History, ChevronDown, ChevronUp } from 'lucide-react';
import { CHANGELOG } from '../lib/changelog';

export default function Home() {
  const [showAllChanges, setShowAllChanges] = useState(false);
  const displayedChangelog = showAllChanges ? CHANGELOG : CHANGELOG.slice(0, 1);

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white">
      {/* Hero Section */}
      <div 
        className="relative overflow-hidden bg-cover bg-center h-[500px] lg:h-[600px]"
        style={{
          backgroundImage: 'url("https://shadowtek-assets.b-cdn.net/images/Depositphotos_3496252_L.jpg")',
        }}
      >
        <div className="absolute inset-0 bg-black/40 backdrop-blur-sm"></div>
        <div className="relative h-full flex items-center">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            <div className="text-center">
              <Shield className="h-16 w-16 sm:h-20 sm:w-20 text-orange-500 mx-auto mb-6 sm:mb-8" />
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tight mb-4 sm:mb-6">
                WAFManager Pro
              </h1>
              <p className="max-w-2xl mx-auto text-lg sm:text-xl text-gray-100 mb-6 sm:mb-8 px-4">
                Enterprise-grade WAF management made simple. Deploy and manage Cloudflare security rules across multiple domains with confidence.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4 sm:space-x-4">
                <Link
                  to="/signup"
                  className="inline-flex items-center justify-center px-6 sm:px-8 py-3 sm:py-4 border border-transparent text-base sm:text-lg font-medium rounded-md shadow-sm text-white bg-orange-600 hover:bg-orange-700 transform transition hover:scale-105"
                >
                  Get Started
                </Link>
                <Link
                  to="/login"
                  className="inline-flex items-center justify-center px-6 sm:px-8 py-3 sm:py-4 border border-white text-base sm:text-lg font-medium rounded-md text-white bg-transparent hover:bg-white/10 transform transition hover:scale-105"
                >
                  Sign In
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Features Grid - Moved up for better flow */}
      <div className="py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-extrabold text-gray-900">
              Why Choose WAFManager Pro?
            </h2>
            <p className="mt-4 text-lg text-gray-500">
              Streamline your web security management with powerful features
            </p>
          </div>

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {[
              {
                icon: Globe,
                title: 'Multi-Domain Management',
                description: 'Deploy and manage WAF rules across multiple domains with a single click. Save time and ensure consistency.'
              },
              {
                icon: Shield,
                title: 'Pre-built Security Rules',
                description: 'Industry-standard WAF rules designed to protect against common threats and malicious traffic.'
              },
              {
                icon: Zap,
                title: 'Real-time Sync Status',
                description: 'Monitor WAF rule deployment status across all domains in real-time. Never miss a security update.'
              },
              {
                icon: Cloud,
                title: 'DNS Proxy Management',
                description: 'Easily manage Cloudflare proxy settings for both root and www domains with visual status indicators.'
              },
              {
                icon: Settings,
                title: 'Customizable Templates',
                description: 'Create and share custom WAF rule templates with your team or the community. Adapt security to your needs.'
              },
              {
                icon: Lock,
                title: 'Enhanced Security',
                description: 'Two-factor authentication, session management, and comprehensive audit logs for enterprise-grade security.'
              }
            ].map((feature, index) => (
              <div key={index} className="relative p-6 bg-white rounded-lg border border-gray-200 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
                <div className="flex items-center mb-4">
                  <feature.icon className="h-6 w-6 text-orange-600" />
                  <h3 className="ml-3 text-lg font-medium text-gray-900">{feature.title}</h3>
                </div>
                <p className="text-gray-500">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Changelog Section */}
      <div className="py-16 sm:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center mb-4">
              <History className="h-8 w-8 text-orange-600 mr-3" />
              <h2 className="text-3xl font-extrabold text-gray-900">Latest Updates</h2>
            </div>
            <p className="text-lg text-gray-500">
              Stay up to date with our latest features and improvements
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="bg-white shadow-lg rounded-lg overflow-hidden border border-gray-200">
              <div className="divide-y divide-gray-200">
                {displayedChangelog.map((release) => (
                  <div key={release.version} className="p-6">
                    <div className="flex items-center space-x-4 mb-4">
                      <h3 className="text-lg font-semibold text-orange-600">
                        v{release.version}
                        {release.version.includes('beta') && (
                          <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            Beta
                          </span>
                        )}
                      </h3>
                      <span className="text-sm text-gray-500">{release.date}</span>
                    </div>
                    <div className="space-y-3">
                      {release.changes.map((change, changeIndex) => (
                        <div key={changeIndex} className="flex items-start">
                          <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                          <span className="text-gray-600">{change}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                <button
                  onClick={() => setShowAllChanges(!showAllChanges)}
                  className="w-full flex items-center justify-center text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
                >
                  {showAllChanges ? (
                    <>
                      <ChevronUp className="h-5 w-5 mr-2" />
                      Show Less
                    </>
                  ) : (
                    <>
                      <ChevronDown className="h-5 w-5 mr-2" />
                      View More Updates
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Rule Set Attribution Section */}
      <div className="py-16 sm:py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-extrabold text-white">
              Powered by Industry Expertise
            </h2>
            <p className="mt-4 text-lg text-gray-300">
              Our WAF rules are built on proven security foundations
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="bg-gray-800 rounded-xl p-8 shadow-lg h-full flex flex-col">
              <div className="mb-8">
                <img 
                  src="https://shadowtek-assets.b-cdn.net/images/webagencyhero-logo-orangewhite.png"
                  alt="Web Agency Hero Logo"
                  className="h-12"
                />
              </div>
              <div className="prose prose-lg flex-grow">
                <p className="text-gray-300">
                  Many of the foundational Cloudflare WAF rules used in WAFManager Pro are inspired by the work of{' '}
                  <a 
                    href="https://webagencyhero.com/cloudflare-waf-rules-v3/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-orange-400 hover:text-orange-300 font-medium"
                  >
                    Web Agency Hero
                  </a>
                  .
                </p>
                <p className="text-gray-300">
                  Their expertise in protecting WordPress websites at scale has been instrumental in shaping our security approach. We're proud to build upon their proven methodologies to deliver enterprise-grade protection for your domains.
                </p>
              </div>
              <div className="mt-8">
                <a 
                  href="https://webagencyhero.com/beginners-guide-to-cloudflare-services-for-agencies/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-6 py-3 border border-orange-400 text-base font-medium rounded-md text-orange-400 hover:bg-orange-400 hover:text-white transition-colors duration-150"
                >
                  Learn More About Their Approach
                </a>
              </div>
            </div>

            <div className="bg-gray-800 rounded-xl p-8 shadow-lg h-full flex flex-col">
              <h3 className="text-xl font-semibold text-white mb-8">Key Security Features</h3>
              <ul className="space-y-6 flex-grow">
                {[
                  'Intelligent bot filtering and management',
                  'Geographic and cloud provider-based access controls',
                  'Advanced crawler management',
                  'WordPress-specific security rules',
                  'Comprehensive hosting provider protection'
                ].map((feature, index) => (
                  <li key={index} className="flex items-start">
                    <CheckCircle className="h-6 w-6 text-orange-400 mt-0.5 mr-3 flex-shrink-0" />
                    <span className="text-gray-300">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Benefits Section */}
      <div className="py-16 sm:py-20 bg-orange-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-extrabold text-gray-900">
              Benefits for Your Business
            </h2>
            <p className="mt-4 text-lg text-gray-500">
              Save time, reduce errors, and improve security across all your domains
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="bg-white p-8 rounded-lg shadow-lg transform transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">
              <h3 className="text-2xl font-semibold text-gray-900 mb-6">For Agencies</h3>
              <ul className="space-y-4">
                {[
                  'Manage multiple client domains efficiently',
                  'Deploy consistent security rules across all sites',
                  'Save time with bulk operations',
                  'Monitor security status in one dashboard',
                  'Create custom templates for different client needs'
                ].map((benefit, index) => (
                  <li key={index} className="flex items-start">
                    <CheckCircle className="h-6 w-6 text-orange-400 mt-0.5 mr-3 flex-shrink-0" />
                    <span className="text-gray-600">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-lg transform transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">
              <h3 className="text-2xl font-semibold text-gray-900 mb-6">For Enterprises</h3>
              <ul className="space-y-4">
                {[
                  'Centralized security management',
                  'Standardized WAF rules across domains',
                  'Reduced configuration errors',
                  'Quick response to security threats',
                  'Comprehensive audit trails'
                ].map((benefit, index) => (
                  <li key={index} className="flex items-start">
                    <CheckCircle className="h-6 w-6 text-orange-400 mt-0.5 mr-3 flex-shrink-0" />
                    <span className="text-gray-600">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-16 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-extrabold text-gray-900 mb-8">
              Ready to secure your domains?
            </h2>
            <div className="flex flex-col sm:flex-row justify-center gap-4 sm:space-x-4">
              <Link
                to="/signup"
                className="inline-flex items-center justify-center px-8 py-4 border border-transparent text-lg font-medium rounded-md shadow-sm text-white bg-orange-600 hover:bg-orange-700 transform transition hover:scale-105"
              >
                Get Started Now
              </Link>
              <Link
                to="/login"
                className="inline-flex items-center justify-center px-8 py-4 border border-gray-300 text-lg font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transform transition hover:scale-105"
              >
                Sign In
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}